import React from 'react';

import {PageLayout} from '../../../components/layout/page-layout';
import {BrowserTabTitle} from '../../../components/window-title/browser-tab-title';
import {getBrowserTabTitle} from '../common/get-browser-tab-title';
import {DocRevisionsPanel} from '../common/version-history/doc-revisions-panel';

import {ViewModeActivityBar} from './activity-bar';
import {DocumentView} from './document-view';
import {ViewModeDocumentsSidebar} from './sidebar/explore-docs-sidebar';
import {useDocumentQuery} from './hooks/requests/use-document-query';
import {usePageLayout} from '../../../components/layout/page-layout/use-page-layout';
import {useSidebarTabs} from '../../../hooks/use-sidebar-tabs';
import {ViewModeTab} from '../../../components/layout/page-layout/layout-typings';
import {useDocRevisionsContext} from '../../../providers/version';
import {useAddRecordToExploreViewSearchHistoryMutation} from './hooks/requests/use-add-record-to-explore-view-search-history-mutation';
import {useSearchDataFromUrl} from './hooks/use-search-data-from-url';
import {SearchData} from '../common/sidebar-search-field/interface';
import {convertSearchDataToSearchHistoryRecord} from './utils';
import {useHandleFindUsages} from '../common/sidebar-search-field/hooks/use-handle-find-usages';
import {GlobalVersionData} from '../../../hooks/global-version/use-global-version-query';
import {useSidebarSearchProps} from '../common/sidebar-search-field/hooks/use-sidebar-search-props';
import {useRoute} from 'wouter';
import {DocumentRouteParams} from '../interface';

interface Props {
    selectedGlobalVersion: GlobalVersionData;
    globalVersions: GlobalVersionData[];
}

export const ExploreViewLayout = (props: Props) => {
    const [, params] = useRoute<DocumentRouteParams>('/:documentId?/*?');
    const {documentId} = params || {};

    const [, revisionParams] = useRoute<DocumentRouteParams>('/:documentId/revisions/:revisionId?');
    const {revisionId} = revisionParams || {};

    const tabSettings = useSidebarTabs({
        layout: 'explore',
        activityTabStorageKey: 'documents.explore.lastTab',
        defaultActivityBarTab: ViewModeTab.FAVORITES
    });

    const addItemToSearchHistoryMutation = useAddRecordToExploreViewSearchHistoryMutation();
    const onSearch = React.useCallback(
        (data: SearchData) => {
            const searchHistoryItem = convertSearchDataToSearchHistoryRecord(data);

            if (searchHistoryItem) {
                addItemToSearchHistoryMutation.mutate(searchHistoryItem);
            }
        },
        [addItemToSearchHistoryMutation]
    );

    const searchSidebarContext = useSidebarSearchProps({
        useDefaultSearch: useSearchDataFromUrl,
        onSearch,
        searchDataGlobalStateVar: 'documents.explore.search'
    });

    const docRevisionsProps = useDocRevisionsContext();
    const documentQuery = useDocumentQuery({
        accession: documentId ?? '',
        globalVersionId: props.selectedGlobalVersion.id,
        revisionId: revisionId,
        enabled: Boolean(documentId)
    });

    const browserTabTitle = getBrowserTabTitle(
        docRevisionsProps.selectedRevision?.documentName ?? documentQuery.data?.name
    );
    const isSidebarExpandable = tabSettings.activityBarTab === 'search';
    const layoutSettings = usePageLayout({
        isHasRightPanel: !!revisionId,
        sidebarSizeStorageKey: 'documents.explore.layoutSize',
        sidebarExpandedStorageKey: isSidebarExpandable
            ? 'documents.explore.sidebarExpanded'
            : undefined,
        sidebarClosedStorageKey: 'documents.explore.sidebarClosed'
    });

    const findUsagesProps = useHandleFindUsages({
        tabSettings,
        currentGlobalVersion: props.selectedGlobalVersion,
        searchSidebarContext,
        searchTab: ViewModeTab.SEARCH,
        openSidebar: layoutSettings.openSidebar
    });

    const closeRevisionsPanelLink = `/${documentId}`;

    const getSelectedRevisionLink = React.useCallback(
        (revision: string) => `/${documentId}/revisions/${revision}`,
        [documentId]
    );

    const getDocumentLink = React.useCallback(
        (docId: string, nodeId?: string) =>
            `/${docId}${nodeId ? `/@${encodeURIComponent(nodeId)}` : ''}`,
        []
    );

    return (
        <BrowserTabTitle text={browserTabTitle}>
            <PageLayout
                data-testid="explore-view"
                rightPanel={
                    <DocRevisionsPanel
                        docRevisionsProps={docRevisionsProps}
                        closePanelLink={closeRevisionsPanelLink}
                        getSelectedRevisionLink={getSelectedRevisionLink}
                        globalVersions={props.globalVersions}
                        mode="explore"
                        revisionId={revisionId}
                    />
                }
                activityBar={
                    <ViewModeActivityBar
                        tabSettings={tabSettings}
                        layoutSettings={layoutSettings}
                    />
                }
                sidebar={
                    <ViewModeDocumentsSidebar
                        tabSettings={tabSettings}
                        layoutSettings={layoutSettings}
                        searchSidebarContext={searchSidebarContext}
                        findUsagesProps={findUsagesProps}
                        selectedGlobalVersion={props.selectedGlobalVersion}
                        getDocumentLink={getDocumentLink}
                        globalVersions={props.globalVersions}
                        documentId={documentId}
                        revisionId={revisionId}
                    />
                }
                layoutSettings={layoutSettings}
            >
                {documentId ? (
                    <DocumentView
                        documentId={documentId}
                        revisionId={revisionId}
                        findUsagesProps={findUsagesProps}
                        selectedGlobalVersion={props.selectedGlobalVersion}
                        documentQuery={documentQuery}
                        key={`${documentId}${props.selectedGlobalVersion.id}${docRevisionsProps.selectedRevision?.revision}`}
                    />
                ) : null}
            </PageLayout>
        </BrowserTabTitle>
    );
};
